import type { MatchState, PlayerId } from '@signalclash/shared';
import {
  FOG_EXPLORED,
  FOG_VISIBLE,
  getMap,
} from '@signalclash/shared';

const PANEL_PX = 180;
const FOG_BG = '#05080c';
const FOG_EXPLORED_ALPHA = 0.45;
const FOG_UNEXPLORED_ALPHA = 0.78;
const COLOR_PLAYER_ZERO = '#4a96ff';
const COLOR_PLAYER_ONE = '#d55a3a';
const COLOR_NEUTRAL = '#5a6470';
const COLOR_OBSTACLE = '#252a31';
// V1.5+ desaturated grey for offline mini-turrets — neutral enough that the
// player reads "no longer live" at a glance (Rule 21) without the dot
// disappearing entirely (it's still a tactical landmark). Same hue for owned
// and enemy offline turrets — the offline state is what matters.
const COLOR_OFFLINE_TURRET = '#6c727a';
// V1.5+ Slice 2 — Cut Link tint for relays currently in a "cut" window.
// Bright red so the player reads "this relay is severed" at a glance — the
// minimap is the at-a-glance view §17 wants for "which relay matters most".
const COLOR_CUT_LINK_RELAY = '#e2543c';
const SQUAD_DOT_PX = 3;
const RELAY_DOT_PX = 4;
const CORE_DOT_PX = 6;
// Mini-turret dots are drawn slightly smaller than relay dots but one pixel
// larger on the 180-px canvas to be distinguishable from squad dots.  On the
// 96-px narrow-portrait canvas the dot is the same 2 px so the turret mark
// doesn't visually vanish, yet it still reads differently from the 3-px squad
// dot because of the desaturated colour.
const MINI_TURRET_DOT_PX = 2;

// Scaled top-down view of the match. Renders cores, relays, own and visible-enemy
// squads, obstacles, and the fog state in the viewer's grid. Click-to-jump moves
// the world camera to the clicked location — wired by GameScene at scene-create
// via bindCamera; left unbound, clicks are no-ops.
//
// Per docs/09_UI_UX.md V1: minimap reads sim snapshots and emits camera intent.
// Per docs/02_GAME_DESIGN.md §5: enemy squads are NOT shown in fogged cells
// (consistent with the world renderer; "no ghost units").
export class Minimap {
  private readonly container: HTMLDivElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D | null;
  // Mutable so the network-mode `setPlayerId` can flip the minimap's
  // viewer to the server-assigned slot.
  private viewerId: PlayerId;
  private latest: MatchState | null = null;
  private centerCamera: ((worldX: number, worldY: number) => void) | null = null;

  constructor(opts?: { playerId?: PlayerId }) {
    this.viewerId = opts?.playerId ?? 0;
    injectStyles();
    this.container = ensureDiv('minimap', 'minimap');
    this.canvas = document.createElement('canvas');
    this.canvas.width = PANEL_PX;
    this.canvas.height = PANEL_PX;
    this.canvas.className = 'minimap__canvas';
    this.container.appendChild(this.canvas);
    this.ctx = this.canvas.getContext('2d');
    // Use pointerup instead of click so touch coordinates are taken from the
    // PointerEvent (accurate on high-DPI displays and touch screens) rather
    // than the synthetic MouseEvent the browser synthesises after a tap (which
    // can be slightly offset due to display-scaling rounding).
    this.canvas.addEventListener('pointerup', this.onCanvasPointerUp);
  }

  setPlayerId(playerId: PlayerId): void {
    this.viewerId = playerId;
    if (this.latest !== null) this.update(this.latest);
  }

  hide(): void {
    this.container.style.display = 'none';
  }

  show(): void {
    this.container.style.display = 'block';
  }

  bindCamera(handler: (worldX: number, worldY: number) => void): void {
    this.centerCamera = handler;
  }

  unbindCamera(): void {
    this.centerCamera = null;
  }

  update(state: MatchState): void {
    this.latest = state;
    this.draw();
  }

  private readonly onCanvasPointerUp = (e: PointerEvent): void => {
    if (this.centerCamera === null) return;
    const state = this.latest;
    if (state === null) return;
    const rect = this.canvas.getBoundingClientRect();
    const nx = (e.clientX - rect.left) / rect.width;
    const ny = (e.clientY - rect.top) / rect.height;
    // V1.5+ — minimap geometry follows the LOADED map, not the legacy
    // 2000×2000 WORLD_WIDTH constant. Crucible is 3000×2400.
    const mapDef = getMap(state.mapId);
    this.centerCamera(nx * mapDef.width, ny * mapDef.height);
  };

  private draw(): void {
    const ctx = this.ctx;
    const state = this.latest;
    if (ctx === null || state === null) return;
    const w = this.canvas.width;
    const h = this.canvas.height;
    // V1.5+ — scale to the loaded map's actual width/height so Crucible
    // (3000×2400) doesn't render letterboxed.
    const map = getMap(state.mapId);
    const sx = w / map.width;
    const sy = h / map.height;

    // Background.
    ctx.fillStyle = '#0d1116';
    ctx.fillRect(0, 0, w, h);

    // Obstacles (purely cosmetic; gives players a sense of the map shape).
    // Reads the map from the snapshot's mapId so the second map renders its
    // own cover layout.
    ctx.fillStyle = COLOR_OBSTACLE;
    for (const o of map.obstacles) {
      ctx.fillRect(o.x * sx, o.y * sy, Math.max(1, o.w * sx), Math.max(1, o.h * sy));
    }

    // Cores — always shown (fixed map landmarks).
    for (const core of Object.values(state.cores)) {
      ctx.fillStyle = colorForOwner(core.ownerId);
      drawCircle(ctx, core.pos.x * sx, core.pos.y * sy, CORE_DOT_PX);
    }

    // Control nodes — color by owner, neutral otherwise. V1.5+ Slice 2 —
    // a relay sitting in a Cut Link window renders the dot in red so the
    // player sees "the chain is broken here" at a glance (Rule 21).
    for (const node of Object.values(state.controlNodes)) {
      const cut = state.tick < node.linkCutUntilTick;
      ctx.fillStyle = cut ? COLOR_CUT_LINK_RELAY : colorForOwner(node.ownerId);
      drawCircle(ctx, node.pos.x * sx, node.pos.y * sy, RELAY_DOT_PX);
    }

    const grid = state.fog[this.viewerId];

    // V1.5+ forward turret network dependency. Mini-turrets show on the
    // minimap so the player can read forward-pressure positions at a glance.
    // Owner colour while online; desaturated grey when offline (per
    // docs/02_GAME_DESIGN.md §15 + Rule 21 one-second readability). Enemy
    // mini-turrets only render when the cell is currently visible — same
    // "no ghost units" rule the world-renderer uses.
    for (const b of Object.values(state.buildings)) {
      if (b.kind !== 'mini_turret') continue;
      if (b.ownerId !== this.viewerId) {
        const cx = Math.floor(b.pos.x / grid.tilePx);
        const cy = Math.floor(b.pos.y / grid.tilePx);
        const idx = cy * grid.cellsX + cx;
        const v = grid.cells[idx] ?? 0;
        if (v !== FOG_VISIBLE) continue;
      }
      ctx.fillStyle = b.isOnline ? colorForOwner(b.ownerId) : COLOR_OFFLINE_TURRET;
      drawCircle(ctx, b.pos.x * sx, b.pos.y * sy, MINI_TURRET_DOT_PX);
    }

    // Squads — own always; enemy only if pos is in a currently-visible cell.
    for (const squad of Object.values(state.squads)) {
      if (squad.ownerId !== this.viewerId) {
        const cx = Math.floor(squad.pos.x / grid.tilePx);
        const cy = Math.floor(squad.pos.y / grid.tilePx);
        const idx = cy * grid.cellsX + cx;
        const v = grid.cells[idx] ?? 0;
        if (v !== FOG_VISIBLE) continue;
      }
      ctx.fillStyle = colorForOwner(squad.ownerId);
      drawCircle(ctx, squad.pos.x * sx, squad.pos.y * sy, SQUAD_DOT_PX);
    }

    // Fog overlay last so it sits above the dots; faint so colors still read.
    this.drawFog(ctx, state, sx, sy);

    // Border.
    ctx.strokeStyle = '#2a3038';
    ctx.lineWidth = 1;
    ctx.strokeRect(0.5, 0.5, w - 1, h - 1);
  }

  private drawFog(
    ctx: CanvasRenderingContext2D,
    state: MatchState,
    sx: number,
    sy: number,
  ): void {
    const grid = state.fog[this.viewerId];
    const cellW = grid.tilePx * sx;
    const cellH = grid.tilePx * sy;
    for (let cy = 0; cy < grid.cellsY; cy++) {
      const rowStart = cy * grid.cellsX;
      const py = cy * cellH;
      for (let cx = 0; cx < grid.cellsX; cx++) {
        const v = grid.cells[rowStart + cx] ?? 0;
        if (v === FOG_VISIBLE) continue;
        ctx.fillStyle = FOG_BG;
        ctx.globalAlpha = v === FOG_EXPLORED ? FOG_EXPLORED_ALPHA : FOG_UNEXPLORED_ALPHA;
        ctx.fillRect(cx * cellW, py, cellW + 1, cellH + 1);
      }
    }
    ctx.globalAlpha = 1;
  }
}

function colorForOwner(ownerId: PlayerId | null): string {
  if (ownerId === 0) return COLOR_PLAYER_ZERO;
  if (ownerId === 1) return COLOR_PLAYER_ONE;
  return COLOR_NEUTRAL;
}

function drawCircle(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
}

function ensureDiv(id: string, className: string): HTMLDivElement {
  const existing = typeof document !== 'undefined' ? document.getElementById(id) : null;
  if (existing instanceof HTMLDivElement) return existing;
  const div = document.createElement('div');
  div.id = id;
  div.className = className;
  document.body.appendChild(div);
  return div;
}

let stylesInjected = false;
function injectStyles(): void {
  if (stylesInjected) return;
  stylesInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    .minimap {
      position: fixed;
      left: 16px;
      bottom: 16px;
      width: ${PANEL_PX}px;
      height: ${PANEL_PX}px;
      border-radius: 6px;
      background: #0d1116;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
      pointer-events: auto;
      z-index: 5;
      user-select: none;
    }
    .minimap__canvas {
      display: block;
      width: 100%;
      height: 100%;
      border-radius: 6px;
      cursor: crosshair;
      image-rendering: pixelated;
    }
    /* V3 mobile: shrink the minimap and hoist it clear of the on-screen
     * action bar at the bottom. A 200×200 minimap eats a sixth of a phone
     * screen in landscape — way too much. Drop to 124 px on phones, 96 px
     * in narrow portrait. The canvas is still tap-friendly thanks to the
     * larger relative click radius at lower zoom. */
    @media (max-width: 820px), (pointer: coarse) {
      .minimap {
        left: 6px;
        bottom: calc(74px + env(safe-area-inset-bottom, 0px));
        width: 124px;
        height: 124px;
      }
    }
    @media (max-width: 460px) {
      .minimap {
        width: 96px;
        height: 96px;
        bottom: calc(70px + env(safe-area-inset-bottom, 0px));
      }
    }
    /* Landscape phones (e.g. 740×360). The 124-px minimap plus 76-px bar
     * safe-zone consume more than half of a 360-px-tall viewport, squeezing
     * the playable battlefield to nothing. Drop to 84 px and hug the corner
     * tighter so the game can actually breathe. */
    @media (orientation: landscape) and (max-height: 480px) and (pointer: coarse) {
      .minimap {
        width: 84px;
        height: 84px;
        left: 4px;
        bottom: calc(60px + env(safe-area-inset-bottom, 0px));
      }
    }
  `;
  document.head.appendChild(style);
}
